import { getCart, psblInsert, plcsInsert, salplInsert } from "../../../connection-manager";
import { getParams } from "@repo/shared-utils/common-utils";
import { cryptoAPICalledData, decryptAESCBC256 } from "@repo/shared-utils/crypto";
import type {
  IBlInsertApiRequest,
  IBlInsertApiResponse,
  IPlcsInsertApiRequest,
  IPlcsInsertApiResponse,
  ISalplInsertApiRequest,
  ISalplInsertApiResponse,
  ICartData,
  ICartItemDetails,
} from "../../../connection-manager";
import { PROD_CODES, getProductFromProdCode, type TypeCartProduct } from "@repo/shared-utils/cart";
import { SSORedirectArgs } from "../../../../apps/trading-mall-app/app/components/ClickHandler/RedirectionConfig.interface";

interface ISetFormData {
  resume: boolean;
  custType: string;
  applicationID?: string;
  product?: string;
  prodCode?: string;
  plcsResData?: IPlcsInsertApiResponse;
  salplResData?: ISalplInsertApiResponse;
}

export type {
  IBlInsertApiRequest,
  IBlInsertApiResponse,
  IPlcsInsertApiRequest,
  IPlcsInsertApiResponse,
  ISalplInsertApiRequest,
  ISalplInsertApiResponse,
};

export const postBlInsert = async (payload: IBlInsertApiRequest) => {
  return psblInsert(payload);
};

export const postPlcsInsert = async (payload: IPlcsInsertApiRequest) => {
  return plcsInsert(payload);
};

export const postSalplInsert = async (payload: ISalplInsertApiRequest) => {
  return salplInsert(payload);
};

export async function getApplicationIdAndProdCode(
  data: SSORedirectArgs,
  prodCodes: TypeCartProduct[]
): Promise<[applicationID?: string, prodCode?: string]> {
  let applicationID = undefined;
  let prodCode = undefined;

  if (data.checkCart) {
    const cart: ICartData = await getCart();
    const firstItem: ICartItemDetails | undefined = cart.data.cartDetails.find((item) =>
      prodCodes.some((product) => PROD_CODES[product]?.types.has(item.prod.toUpperCase()))
    );
    if (firstItem) {
      applicationID = String(firstItem.appId ?? "");
      prodCode = String(firstItem.prod ?? "");
    }
  } else {
    applicationID = data.data?.applicationId;
    prodCode = data.data?.prodCode;
  }

  return [applicationID, prodCode];
}
export async function getApplicationId(prodCodes: TypeCartProduct[]): Promise<string | undefined> {
  const cart: ICartData = await getCart();
  const foundItems: ICartItemDetails[] = [];
  for (const product of prodCodes) {
    const items = cart.data.cartDetails?.filter((item) =>
      PROD_CODES[product]?.types.has(item.prod.toUpperCase())
    );
    if (items?.length) foundItems.push(...items);
  }
  if (foundItems.length) {
    return foundItems[0]?.appId;
  }
}

export const inAppModuleMap: Record<string, string> = {
  DL: "PL",
  CA: "PL",
  LFL: "PL",
  SALPL: "B2C",
  PLCS: "B2C",
  BL: "BUSINESS_LOAN",
  DEPOSIT: "DEPOSIT",
  BLSE: "BLSE",
  LAMF: "LAMF",
  HL: "HL_FRESH",
  HLBT: "HL_BT",
  BLSEBT: "BLSE/BT",
  MFI: "MFI",
  LAP: "LAPX",
  LAPBT: "LAPX",
  EMI: "EMI",
  DC: "DEBT",
};

export const HLPDPConstants = {
  BALANCE_TRANSFER: "balance-transfer",
} as const;

export async function setUtmParamsForApp(): Promise<{
  utm_source: string;
  utm_campaign: string;
  utm_medium: string;
}> {
  const utmSource = getParams("utm_source");
  const utmCampaign = getParams("utm_campaign");
  let utmMedium = getParams("utm_medium");

  if (utmSource?.toLowerCase() === "referandearn" && utmMedium) {
    try {
      const { REQ_BFL_REFER_KEY, REQ_BFL_REFER_IV } = await cryptoAPICalledData();
      if (REQ_BFL_REFER_KEY && REQ_BFL_REFER_IV) {
        utmMedium = utmMedium.replace(/~/g, "+").replace(/_/g, "/");
        utmMedium = await decryptAESCBC256(utmMedium, REQ_BFL_REFER_KEY, REQ_BFL_REFER_IV);
      }
    } catch (err) {
      console.error("Error decrypting utm_medium:", err);
      utmMedium = "NA";
    }
  }

  return {
    utm_source: utmSource ?? "NA",
    utm_campaign: utmCampaign ?? "NA",
    utm_medium: utmMedium ?? "NA",
  };
}

export async function redirectionInAppWebform(
  appId: string,
  product: string,
  fdProdCode?: string,
  isResume?: boolean
): Promise<void> {
  const utmParams = await setUtmParamsForApp();
  let appData: {
    appId: string | undefined;
    type?: string;
    module: string;
    resume?: boolean;
    utmParams: { utm_source: string; utm_campaign: string; utm_medium: string };
  } = {
    appId: appId,
    module: inAppModuleMap[product] || product,
    utmParams: utmParams,
  };

  if (product === "CA" || product === "DL" || product === "LFL") {
    appData = {
      ...appData,
      type: product === "LFL" ? "Lawyer" : product,
    };
  }

  if (product === "DEPOSIT") {
    appData = {
      ...appData,
      type: fdProdCode || "",
    };
  }

  if (product === "HL") {
    const isBalanceTransfer = globalThis.window?.location?.href?.includes(
      HLPDPConstants.BALANCE_TRANSFER
    );
    appData = {
      ...appData,
      module: isBalanceTransfer ? "HL_BT" : "HL_FRESH",
      resume: isResume,
    };
  }

  if (product === "DC") {
    appData = {
      ...appData,
      type: "DOCTOR_DEBT",
    };
  }

  const inAppRedirectionPrompt = `inAppDeepLink://bajajsuperapp://dyninApp/${inAppModuleMap[product]}${
    product === "BLSEBT" ? "" : "/1"
  }/[{"encryptionKey":"","payload":"${encodeURIComponent(
    JSON.stringify(appData)
  )}","module":"test","operation":"custRelationshipDetails"}]/${encodeURIComponent(JSON.stringify(appData))}`;

  (
    globalThis.window as Window & {
      tarsier?: { invokePrompt: (prompt: string, force?: boolean) => unknown };
    }
  )?.tarsier?.invokePrompt(inAppRedirectionPrompt, true);
}

export const renderProduct = (
  prodCode: string | undefined,
  icorsProduct: string | undefined
): string => (prodCode ? getProductFromProdCode(prodCode) || "SALPL" : icorsProduct || "SALPL");

export const setFormData = ({
  custType,
  applicationID,
  product,
  prodCode,
  plcsResData,
  salplResData,
  resume,
}: ISetFormData) => {
  return {
    custType,
    product,
    applicationID: plcsResData?.id ?? salplResData?.id ?? applicationID ?? "",
    prodCode: prodCode ?? "",
    resume: plcsResData?.id || salplResData?.id || applicationID ? resume : false,
  } as const;
};
